import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Badge } from '@/components/ui/badge'
import { CircleAlert } from 'lucide-react'

const readinessCopy = {
  waiting_for_collection: {
    badge: 'Waiting for products',
    title: 'Waiting for products',
    description:
      'This update won’t go live until a matching collection has visible products. It will become ready automatically as products are added.',
  },
  waiting_for_content: {
    badge: 'Waiting for seasonal content',
    title: 'Waiting for seasonal content',
    description:
      'This update won’t go live until a seasonal homepage banner has been published for its occasion.',
  },
}

export function getSeasonalReadinessCopy(design) {
  const readiness = design?.seasonal?.readiness

  if (!readiness || readiness.ready || !readiness.reason) {
    return null
  }

  return readinessCopy[readiness.reason] ?? null
}

export function SeasonalReadinessBadge({ design, isEnabled }) {
  const copy = isEnabled ? getSeasonalReadinessCopy(design) : null

  if (!copy) {
    return null
  }

  return (
    <Badge variant="warning">
      <CircleAlert className="size-3.5" />
      {copy.badge}
    </Badge>
  )
}

export function SeasonalReadinessNotice({ design, isEnabled }) {
  const copy = getSeasonalReadinessCopy(design)

  if (!copy) {
    return null
  }

  return (
    <Alert variant="warning">
      <CircleAlert />
      <AlertTitle>{isEnabled ? `Scheduled, ${copy.title.toLowerCase()}` : copy.title}</AlertTitle>
      <AlertDescription>{copy.description}</AlertDescription>
    </Alert>
  )
}

export function SeasonalReadinessSummary({ designs, onDismiss = null, schedules }) {
  const waitingDesigns = designs.filter((design) => {
    const schedule = schedules?.[`design_${design.id}`]
    return Boolean(schedule?.is_enabled) && getSeasonalReadinessCopy(design)
  })

  if (!waitingDesigns.length) {
    return null
  }

  const reasons = new Set(waitingDesigns.map((design) => design.seasonal.readiness.reason))
  const count = waitingDesigns.length
  const title =
    reasons.size > 1
      ? 'Some updates aren’t ready yet'
      : reasons.has('waiting_for_collection')
        ? 'Some updates are waiting for products'
        : 'Some updates are waiting for seasonal content'
  const requirement =
    reasons.size > 1
      ? 'their seasonal content and product requirements are ready'
      : reasons.has('waiting_for_collection')
        ? 'a matching collection has visible products'
        : 'seasonal homepage banner content is published'

  return (
    <InfoNotice
      compact
      tone="warning"
      title={title}
      icon={<CircleAlert />}
      onDismiss={onDismiss}
      dismissLabel="Dismiss seasonal update readiness notice"
    >
      {count === 1 ? 'One enabled update won’t' : `${count} enabled updates won’t`} go live until{' '}
      {requirement}. They’ll become ready automatically.
    </InfoNotice>
  )
}
